import type { EventBus } from "../core/EventBus.ts";
import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { ActiveSynergy } from "../entities/types.ts";
import { isStealthHidden } from "./StealthGuard.ts";

/** All synergy effect.type values handled by updateSynergyEffects. */
export const HANDLED_SYNERGY_EFFECTS: ReadonlySet<string> = new Set([
  "aoeSlowDamage",
  "beamDamage",
  "stunOnHit",
  "statBoostAura",
  "aoe_pulse",
  "autoTargetMarked",
  "aoeMark",
  "markedDotBoost",
  "chargeReduction",
  "chainOnKill",
  "dualDot",
  "enhancedSpread",
  "unlimitedSpreadOnMarked",
]);

/**
 * Applies synergy effects (damage, slow, stun, aura) to enemies
 * based on active synergy state.
 */

export function updateSynergyEffects(
  activeSynergies: ActiveSynergy[],
  dt: number,
  enemies: Enemy[],
  frequencyMultiplier: number,
  triComboAutoFire: boolean,
  bus: EventBus,
  effectMultiplier = 1,
  cascadeDamage = 0,
): void {
  for (const synergy of activeSynergies) {
    synergy._timer = (synergy._timer ?? 0) + dt;
    const interval =
      (synergy.effect.tickInterval ?? 1000) / frequencyMultiplier;
    if (synergy._timer < interval) continue;
    synergy._timer = 0;

    switch (synergy.effect.type) {
      case "aoeSlowDamage":
        applyAoeSlow(synergy, enemies, bus, effectMultiplier);
        break;
      case "beamDamage":
        applyBeamDamage(synergy, enemies, bus, effectMultiplier);
        break;
      case "stunOnHit":
        // Passive — GA-23 (D-321): rolled by DamageCalculation's
        // rollElectroshockStun() on every damage-dealing hit that lands
        // within an Electroshock tower's range. The direct projectile-impact
        // path (applyProjectileDamage) is element-agnostic — no lightning
        // check there, any element's hit can roll the stun; only the
        // chain-bounce path (chainHit) is gated to lightning damage.
        break;
      case "statBoostAura":
        applyAuraDamage(synergy, enemies, bus, effectMultiplier);
        break;
      case "aoe_pulse":
        applyResonancePulse(synergy, enemies, bus, effectMultiplier);
        break;
      case "autoTargetMarked":
      case "aoeMark":
        // These are passive synergies — handled externally by CombatSystem
        break;
      case "markedDotBoost":
        // CR-H8: passive — handled by DamageCalculation.applySpecial
        // case "dot" via synergySystem.getMarkedDotBoost()
        break;
      case "chargeReduction":
      case "chainOnKill":
        // Passive — handled by SniperCombat (designated charge/targeting, railarc on kill)
        break;
      case "dualDot":
      case "enhancedSpread":
      case "unlimitedSpreadOnMarked":
        // Passive — handled via SynergySystem.getVirusSynergyMods() in virus systems.
        break;
    }

    if (triComboAutoFire && synergy.towers.length === 3) {
      triComboFire(synergy, enemies, bus, effectMultiplier);
    }

    // CASCADE (BL-72): each synergy trigger splashes flat AoE to nearby enemies.
    // cascadeDamage is 0 unless the CASCADE card is active → no-op + determinism-
    // neutral otherwise.
    if (cascadeDamage > 0) {
      applyCascadeSplash(synergy, enemies, bus, cascadeDamage);
    }
  }
}

const CASCADE_RADIUS = 96; // ~2 cells around the synergy centre

/** CASCADE (BL-72): flat AoE splash around a triggering synergy's centre.
 *  Mirrors the other synergy-damage effects — takeDamage + deactivate on kill,
 *  no enemy.killed emit (synergy damage is bonus, not gold-rewarding). */
function applyCascadeSplash(
  synergy: ActiveSynergy,
  enemies: Enemy[],
  bus: EventBus,
  damage: number,
): void {
  const rSq = CASCADE_RADIUS * CASCADE_RADIUS;
  let anchor: Enemy | null = null;
  for (const enemy of enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;
    const dx = enemy.x - synergy.centerX;
    const dy = enemy.y - synergy.centerY;
    if (dx * dx + dy * dy > rSq) continue;
    const killed = enemy.takeDamage(damage, "kinetic");
    if (!anchor) anchor = enemy; // first hit → particle anchor (valid even if killed)
    if (killed) enemy.active = false;
  }
  if (anchor) {
    bus.emit("synergy.triggered", { synergy, target: anchor, type: "cascade" });
  }
}

export function getStunChanceForTower(
  tower: Tower,
  activeSynergies: readonly ActiveSynergy[],
): number {
  for (const syn of activeSynergies) {
    if (syn.effect.type !== "stunOnHit") continue;
    if (syn.towers.some((t) => t === tower)) return syn.effect.stunChance ?? 0;
  }
  return 0;
}

export function getDesignatedConfig(
  tower: Tower,
  activeSynergies: readonly ActiveSynergy[],
): { chargeMultiplierForMarked: number; prioritizeMarked: boolean } | null {
  for (const syn of activeSynergies) {
    if (syn.effect.type !== "chargeReduction") continue;
    if (syn.towers.some((t) => t === tower)) {
      return {
        chargeMultiplierForMarked: syn.effect.chargeMultiplierForMarked ?? 0.5,
        prioritizeMarked: syn.effect.prioritizeMarked ?? true,
      };
    }
  }
  return null;
}

export function getRailArcSynergy(
  tower: Tower,
  activeSynergies: readonly ActiveSynergy[],
): ActiveSynergy | null {
  for (const syn of activeSynergies) {
    if (syn.effect.type !== "chainOnKill") continue;
    if (syn.towers.some((t) => t === tower)) return syn;
  }
  return null;
}

export function applyRailArc(
  synergy: ActiveSynergy,
  originX: number,
  originY: number,
  baseDamage: number,
  enemies: Enemy[],
  bus: EventBus,
  alreadyHit: Set<Enemy>,
): void {
  const arcTargets = synergy.effect.arcTargets ?? 2;
  const arcDamageDecay = synergy.effect.arcDamageDecay ?? 0.4;
  const arcRadius = synergy.effect.arcRadius ?? 80;
  const arcRadiusSq = arcRadius * arcRadius;

  let arcX = originX;
  let arcY = originY;

  for (let jump = 1; jump <= arcTargets; jump++) {
    let nearest: Enemy | null = null;
    let nearestDistSq = Infinity;

    for (const enemy of enemies) {
      if (!enemy.active) continue;
      if (isStealthHidden(enemy)) continue;
      if (alreadyHit.has(enemy)) continue;

      const dx = enemy.x - arcX;
      const dy = enemy.y - arcY;
      const distSq = dx * dx + dy * dy;
      if (distSq <= arcRadiusSq && distSq < nearestDistSq) {
        nearest = enemy;
        nearestDistSq = distSq;
      }
    }

    if (!nearest) break;

    alreadyHit.add(nearest);
    const damage = Math.round(baseDamage * arcDamageDecay ** jump);
    const killed = nearest.takeDamage(damage, "lightning");
    if (killed) nearest.active = false;

    bus.emit("synergy.triggered", {
      synergy,
      target: nearest,
      type: "railarc",
    });

    arcX = nearest.x;
    arcY = nearest.y;
  }
}

function applyAoeSlow(
  synergy: ActiveSynergy,
  enemies: Enemy[],
  bus: EventBus,
  effectMultiplier: number,
): void {
  const { centerX, centerY } = synergy;
  const radius = synergy.effect.radius ?? 60;
  const radiusSq = radius * radius;

  for (const enemy of enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;
    const dx = enemy.x - centerX;
    const dy = enemy.y - centerY;
    if (dx * dx + dy * dy <= radiusSq) {
      enemy.applySlow(
        synergy.effect.slowFactor ?? 0.6,
        synergy.effect.slowDuration ?? 2000,
      );
      const baseDmg = synergy.effect.damage ?? 8;
      const killed = enemy.takeDamage(
        Math.round(baseDmg * effectMultiplier),
        "steam",
      );
      if (killed) enemy.active = false;
      bus.emit("synergy.triggered", {
        synergy,
        target: enemy,
        type: "aoeSlowDamage",
      });
    }
  }
}

function applyBeamDamage(
  synergy: ActiveSynergy,
  enemies: Enemy[],
  bus: EventBus,
  effectMultiplier: number,
): void {
  let closest: Enemy | null = null;
  let closestDistSq = Infinity;
  // Plain loop instead of Math.max(...map(...)) to avoid the array + spread
  // allocation on every beam tick (P48-P1). Identical result.
  let maxRange = 0;
  for (const t of synergy.towers) {
    const r = (t as Tower).range;
    if (r > maxRange) maxRange = r;
  }
  const maxRangeSq = maxRange * maxRange;

  for (const enemy of enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;
    const dx = enemy.x - synergy.centerX;
    const dy = enemy.y - synergy.centerY;
    const distSq = dx * dx + dy * dy;
    if (distSq < closestDistSq && distSq <= maxRangeSq) {
      closest = enemy;
      closestDistSq = distSq;
    }
  }

  if (closest) {
    const towerDmg = synergy.towers.reduce(
      (sum, t) => sum + (t as Tower).damage,
      0,
    );
    const damage = Math.round(
      towerDmg *
        (synergy.effect.damageMultiplier ?? 1.5) *
        0.5 *
        effectMultiplier,
    );
    const killed = closest.takeDamage(damage, "plasma");
    if (killed) closest.active = false;
    bus.emit("synergy.triggered", {
      synergy,
      target: closest,
      type: "beamDamage",
    });
  }
}

function applyAuraDamage(
  synergy: ActiveSynergy,
  enemies: Enemy[],
  bus: EventBus,
  effectMultiplier: number,
): void {
  const { centerX, centerY } = synergy;
  const radius = synergy.effect.auraRadius ?? 50;
  const radiusSq = radius * radius;

  for (const enemy of enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;
    const dx = enemy.x - centerX;
    const dy = enemy.y - centerY;
    if (dx * dx + dy * dy <= radiusSq) {
      const baseDmg = synergy.effect.auraDamage ?? 12;
      const killed = enemy.takeDamage(
        Math.round(baseDmg * effectMultiplier),
        "nexus",
      );
      if (killed) enemy.active = false;
      bus.emit("synergy.triggered", {
        synergy,
        target: enemy,
        type: "auraDamage",
      });
    }
  }
}

function applyResonancePulse(
  synergy: ActiveSynergy,
  enemies: Enemy[],
  bus: EventBus,
  effectMultiplier: number,
): void {
  const { centerX, centerY } = synergy;
  const radius = (synergy.effect.radius ?? 2) * 48; // cells → pixels
  const radiusSq = radius * radius;

  for (const enemy of enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;
    const dx = enemy.x - centerX;
    const dy = enemy.y - centerY;
    if (dx * dx + dy * dy <= radiusSq) {
      const baseDmg = synergy.effect.damage ?? 10;
      const killed = enemy.takeDamage(
        Math.round(baseDmg * effectMultiplier),
        "nexus",
      );
      if (killed) enemy.active = false;
      bus.emit("synergy.triggered", {
        synergy,
        target: enemy,
        type: "aoe_pulse",
      });
    }
  }
}

function triComboFire(
  synergy: ActiveSynergy,
  enemies: Enemy[],
  bus: EventBus,
  effectMultiplier: number,
): void {
  let nearest: Enemy | null = null;
  let nearestDistSq = Infinity;
  const range = 150;
  const rangeSq = range * range;

  for (const enemy of enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;
    const dx = enemy.x - synergy.centerX;
    const dy = enemy.y - synergy.centerY;
    const distSq = dx * dx + dy * dy;
    if (distSq < rangeSq && distSq < nearestDistSq) {
      nearest = enemy;
      nearestDistSq = distSq;
    }
  }

  if (nearest) {
    const avgDamage = Math.round(
      (synergy.towers.reduce((sum, t) => sum + (t as Tower).damage, 0) / 3) *
        effectMultiplier,
    );
    bus.emit("synergy.autoFire", {
      x: synergy.centerX,
      y: synergy.centerY,
      target: nearest,
      damage: avgDamage,
      element: "fire",
    });
  }
}
